import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getApiClient } from '@signalvote/api-client';
import { readsMeta } from '@signalvote/data';
import {
  inviteUserRequest,
  updateCampaignRequest,
  updateCapabilitiesRequest,
  type CampaignDetailDto,
  type ScoutAccessDto,
  type UpdateCampaignRequest,
  type PendingInvitationDto,
  type UpdateCapabilitiesRequest,
  type SaveScoutAccessRequest,
} from '@signalvote/shared';
import { queryKeys } from '@/lib/queryKeys';
import { useCampaignStore } from '@/stores';
import { defineQuery, defineWrite, edit, requireSelectedCampaign } from '@/lib/data';

const api = getApiClient();

/**
 * Fetch the current user's campaigns
 */
export function useMyCampaigns() {
  return useQuery(myCampaignsQuery());
}

/**
 * The user's campaigns, synced into the campaign store so the selected campaign carries what the
 * server has now. Not a `defineQuery`: the web layer keys and idles every defined read on the
 * selected campaign, and this is the list a campaign is selected from. Its `meta` still lets a
 * write set naming one of its entity types refresh it.
 */
export function myCampaignsQuery() {
  return {
    queryKey: queryKeys.tenantCampaigns.my(),
    queryFn: async () => {
      const campaigns = await getApiClient().tenantCampaigns.getMyCampaigns();
      useCampaignStore.getState().setCampaigns(campaigns);
      return campaigns;
    },
    staleTime: 5 * 60 * 1000,
    meta: readsMeta('GET /api/campaigns/my'),
  };
}

/** The selected campaign's details. */
export const useCurrentCampaign = defineQuery({
  key: () => ['tenant-campaigns', 'current'],
  reads: 'GET /api/campaigns/current',
  fetch: (_: undefined, { campaignId }): Promise<CampaignDetailDto> => {
    requireSelectedCampaign(campaignId);
    return api.tenantCampaigns.getCurrentCampaign();
  },
  staleTime: 5 * 60 * 1000,
});

/**
 * Update a campaign. The campaign reads refresh from the write set; the store copy of the
 * selected campaign takes the sent fields at once, so the header and theme don't wait on a refetch.
 */
export const useUpdateTenantCampaign = defineWrite({
  send: ({ id, data }: { id: string; data: Partial<UpdateCampaignRequest> }) =>
    api.tenantCampaigns.updateCampaign(id, updateCampaignRequest(data)),
  onSuccess: (_, { id, data }) => {
    const { selectedCampaign, selectCampaign } = useCampaignStore.getState();
    if (selectedCampaign?.id !== id) return;
    const definedUpdates = Object.fromEntries(Object.entries(data).filter(([, v]) => v !== undefined));
    selectCampaign({ ...selectedCampaign, ...definedUpdates });
  },
});

/**
 * Set/unset a campaign's demo flag (ADR-056). Platform-admin-only: the backend 403s any
 * non-admin, a campaign owner included. The write set refreshes the campaign list, whose refetch
 * re-syncs `selectedCampaign`, so the DEMO banner flips in-session.
 */
export const useSetCampaignDemo = defineWrite({
  send: ({ id, isDemo }: { id: string; isDemo: boolean }) => api.admin.campaigns.setDemo(id, isDemo),
});

/** The campaign's assistant switch and allowlist, for a platform admin. */
export const useScoutAccess = defineQuery({
  key: (campaignId: string) => ['tenant-campaigns', 'scout-access', campaignId],
  reads: 'GET /api/Admin/campaigns/{campaignId}/assistant-access',
  fetch: (campaignId: string): Promise<ScoutAccessDto> => api.admin.campaigns.getScoutAccess(campaignId),
});

/**
 * Save the switch and the whole allowlist. The response carries the fresh token and lands in the
 * access read, so the next save sends it; the campaign list refreshes from the write set, since
 * each campaign's assistant flag is per user.
 */
export const useUpdateScoutAccess = defineWrite({
  send: ({ campaignId, request }: { campaignId: string; request: SaveScoutAccessRequest }) =>
    api.admin.campaigns.updateScoutAccess(campaignId, request),
  land: (saved, { campaignId }) => [edit(useScoutAccess, campaignId, () => saved)],
});

/** Invite a user to a campaign. The server answers InvitationToken, which the pending invites read declares. */
export const useInviteUser = defineWrite({
  send: ({ campaignId, data }: { campaignId: string; data: Parameters<typeof inviteUserRequest>[0] }) =>
    api.tenantCampaigns.inviteUser(campaignId, inviteUserRequest(data)),
});

/** Transfer campaign ownership to another member. The staff list and the campaign reads refresh from the write set. */
export const useTransferOwnership = defineWrite({
  send: ({ campaignId, newOwnerUserId }: { campaignId: string; newOwnerUserId: string }) =>
    api.tenantCampaigns.transferOwnership(campaignId, { newOwnerUserId }),
});

/** The selected campaign's invitations not yet accepted. */
export const usePendingInvitations = defineQuery({
  key: () => ['tenant-campaigns', 'invitations'],
  reads: 'GET /api/campaigns/{id}/invitations',
  fetch: (_: undefined, { campaignId }): Promise<PendingInvitationDto[]> => {
    requireSelectedCampaign(campaignId);
    return api.tenantCampaigns.getInvitations(campaignId!);
  },
});

export const useResendInvitation = defineWrite({
  send: ({ campaignId, invitationId }: { campaignId: string; invitationId: string }) =>
    api.tenantCampaigns.resendInvitation(campaignId, invitationId),
});

export const useCancelInvitation = defineWrite({
  send: ({ campaignId, invitationId }: { campaignId: string; invitationId: string }) =>
    api.tenantCampaigns.cancelInvitation(campaignId, invitationId),
});

/**
 * Update a user's capabilities in a campaign. The staff list refreshes from the write set; the
 * vendor list's GET is not a defined read yet, so it keeps a hand invalidation.
 */
export function useUpdateUserCapabilities() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ campaignId, userId, data }: { campaignId: string; userId: string; data: Partial<UpdateCapabilitiesRequest> }) =>
      api.tenantCampaigns.updateUserCapabilities(campaignId, userId, updateCapabilitiesRequest(data)),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.vendors.all,
      });
    },
  });
}
